import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Plus, Trash2, X, Check, XCircle, Clock, ShieldCheck, User, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { DatePicker } from "@/components/ui/date-picker";
import { useModalGuard, UnsavedGuardBar } from "@/hooks/useModalGuard";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";

const statusMeta = {
  pending_manager: { label: "Menunggu Manager", chip: "bg-amber-100 text-amber-800" },
  pending_hr: { label: "Menunggu HR", chip: "bg-sky-100 text-sky-800" },
  approved: { label: "Disetujui", chip: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Ditolak", chip: "bg-rose-100 text-rose-800" },
  pending: { label: "Menunggu", chip: "bg-amber-100 text-amber-800" }, // legacy
};

export default function Cuti() {
  const { user, can } = useAuth();
  // Kunci modul `cuti` — SAMA PERSIS dengan gerbang backend (routers/cuti.py).
  // Sebelumnya semuanya digantung pada `user?.role !== "viewer"`, yang hampir
  // selalu true: tombolnya tampil untuk siapa saja lalu berujung 403, sementara
  // HR yang izinnya sudah dicentang justru tidak pernah bisa memakainya karena
  // endpoint create-nya mengecek role. Dipisah per aksi karena backend memang
  // memakai aksi berbeda: create (POST), update (approve/reject), delete (DELETE).
  const canCreate = can("cuti", "create");
  const canUpdate = can("cuti", "update");
  const canDelete = can("cuti", "delete");
  const [items, setItems] = useState([]);
  const [emps, setEmps] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ employee_id: "", jenis: "Tahunan", tanggal_mulai: "", tanggal_selesai: "", jml_hari: 1, keterangan: "", use_skd: false, surat_dokter_url: "" });
  const [filterStatus, setFilterStatus] = useState("");
  // Cari cuti per nama karyawan (brief owner 29 Agu). Disaring di layar dengan
  // sadar: daftar cuti diambil utuh dari `/cuti` tanpa batas baris, jadi tidak
  // ada baris yang bisa "hilang di halaman berikutnya" seperti pada daftar
  // piutang. Kalau endpointnya nanti dipotong, pencarian ini HARUS pindah ke
  // server.
  const [cariNama, setCariNama] = useState("");
  const [tab, setTab] = useState("all"); // all | cuti | skd

  // Tanpa .catch, kegagalan di sini jadi unhandled rejection tanpa pesan apa pun —
  // layar tinggal kosong dan user menyangka aplikasinya rusak.
  const load = () => {
    api.get("/cuti").then(r => setItems(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Gagal memuat daftar cuti"));
    api.get("/employees").then(r => setEmps(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Gagal memuat daftar karyawan"));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (form.use_skd && !form.surat_dokter_url.trim()) {
        toast.error("SKD wajib melampirkan URL surat dokter"); return;
      }
      const { data } = await api.post("/cuti", form);
      const needsManager = data.requires_manager;
      toast.success(needsManager ? "Pengajuan cuti panjang dikirim → butuh approval Manager, lalu HR" : "Pengajuan cuti dikirim → menunggu approval HR");
      setModal(false); setForm({ employee_id: "", jenis: "Tahunan", tanggal_mulai: "", tanggal_selesai: "", jml_hari: 1, keterangan: "", use_skd: false, surat_dokter_url: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  // Form 7 field (karyawan, jenis, tanggal, SKD, keterangan) — JSX modal ada
  // di komponen halaman yang tidak unmount, jadi `active` mengikuti `modal`.
  const cutiGuard = useModalGuard({
    active: modal, dangerous: true,
    onClose: () => setModal(false), onSubmit: save,
  });

  const approve = async (id) => { try { const { data } = await api.patch(`/cuti/${id}/approve`); toast.success(data.next === "approved" ? "Cuti disetujui final" : "Approval Manager tersimpan, menunggu HR"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } };
  const reject = async (id) => { try { await api.patch(`/cuti/${id}/reject`); toast.success("Cuti ditolak"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } };
  // Sama seperti approve/reject di atas: tanpa catch, 403/500 di sini tidak
  // memunculkan apa pun — baris cuti tetap terpampang dan staf mengira klik-nya
  // tidak terbaca, lalu mengulanginya.
  const del = async (id) => {
    if (!confirm("Hapus?")) return;
    try { await api.delete(`/cuti/${id}`); toast.success("Cuti dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus"); }
  };

  // Backend approve/reject memakai require_authz_or_supervisor(): lolos kalau punya
  // izin `cuti.update` ATAU jadi atasan (langsung/berjenjang) karyawan itu — lihat
  // deps.py::_is_supervisor_of yang tetap jadi satu-satunya penentu sebenarnya.
  // Penelusuran di sini cuma memutuskan tombolnya digambar atau tidak, dan sengaja
  // dibuat MURAH HATI: kalau daftar karyawan belum/tidak terbaca, tombolnya tetap
  // ditampilkan supaya SPV/Head Store tidak mendadak kehilangan tombol approve yang
  // selama ini dia pakai. Yang benar-benar menolak tetap backend.
  const adalahAtasanDari = (employeeId) => {
    if (!employeeId || emps.length === 0) return true; // data belum ada → jangan menebak "tidak boleh"
    const emailSaya = (user?.email || "").toLowerCase();
    const empSayaId = user?.employee_id || null;
    if (!emailSaya && !empSayaId) return false;
    const byId = new Map(emps.map(e => [e.id, e]));
    let cur = byId.get(employeeId);
    for (let i = 0; i < 10 && cur; i++) {
      const atasanId = cur.atasan_id;
      if (!atasanId) return false;
      if (empSayaId && atasanId === empSayaId) return true;
      const atasan = byId.get(atasanId);
      if (!atasan) return false;
      if (emailSaya && (atasan.email || "").toLowerCase() === emailSaya) return true;
      cur = atasan;
    }
    return false;
  };
  const bolehApprove = (c) => canUpdate || adalahAtasanDari(c.employee_id);

  const tabFiltered = items.filter(c => {
    if (tab === "skd") return !!c.use_skd;
    if (tab === "cuti") return !c.use_skd;
    return true;
  });
  const namaCari = cariNama.trim().toLowerCase();
  const filtered = tabFiltered
    .filter(c => !filterStatus || (c.status || "approved") === filterStatus)
    .filter(c => !namaCari || (c.employee_nama || c.nama || "").toLowerCase().includes(namaCari));
  const pendingCount = tabFiltered.filter(c => c.status === "pending_manager" || c.status === "pending_hr" || c.status === "pending").length;
  // Urutan bawaan dipertahankan sampai header diklik.
  const { sortKey, sortDir, toggle } = useSortState();
  const sortedFiltered = sortRows(filtered, sortKey, sortDir, {
    nama: (c) => c.employee_nama, periode: (c) => c.tanggal_mulai, hari: (c) => c.jml_hari,
  });

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Cuti</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Cuti Karyawan</h1>
          <p className="mt-2 text-sm text-slate-500">
            Cuti ≤3 hari cukup approval HR. Cuti &gt;3 hari butuh <span className="font-medium">Manager → HR</span> (2 user berbeda).
            {pendingCount > 0 && <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800 font-medium"><Clock className="w-3 h-3" /> {pendingCount} menunggu approval</span>}
          </p>
        </div>
        {canCreate && (
          <button data-testid="btn-add-cuti" onClick={() => setModal(true)}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium transition-colors">
            <Plus className="w-4 h-4" /> Ajukan Cuti
          </button>
        )}
      </header>

      <div className="mb-4 flex items-center gap-2 border-b border-slate-200">
        {[["all", `Semua (${items.length})`], ["cuti", `Cuti Biasa (${items.filter(c => !c.use_skd).length})`], ["skd", `SKD - Surat Dokter (${items.filter(c => c.use_skd).length})`]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`cuti-tab-${v}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === v ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>{l}</button>
        ))}
      </div>

      {/* Cari cuti per nama karyawan (brief owner 29 Agu). */}
      <div className="mb-4 flex items-center gap-2 max-w-sm h-10 px-3 rounded-lg border border-slate-200 bg-white">
        <Search className="w-4 h-4 text-slate-400 shrink-0" />
        <input value={cariNama} onChange={e => setCariNama(e.target.value)}
          data-testid="cuti-cari-nama" placeholder="Cari nama karyawan..."
          className="flex-1 text-sm outline-none bg-transparent" />
        {cariNama && (
          <button onClick={() => setCariNama("")} className="text-slate-400 hover:text-slate-700">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="mb-4 flex items-center flex-wrap gap-2">
        {[["", "Semua"], ["pending_manager", "Menunggu Manager"], ["pending_hr", "Menunggu HR"], ["approved", "Disetujui"], ["rejected", "Ditolak"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilterStatus(v)} data-testid={`cuti-filter-${v || "all"}`}
            className={`h-8 px-3 rounded-lg text-xs font-medium transition-colors ${
              filterStatus === v ? "bg-[#0F172A] text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}>{l}</button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <SortTh label="Nama" sortKeyName="nama" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cuti-sort-nama" />
              <SortTh label="Jenis" sortKeyName="jenis" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cuti-sort-jenis" />
              <SortTh label="Periode" sortKeyName="periode" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cuti-sort-periode" />
              <SortTh label="Hari" sortKeyName="hari" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="cuti-sort-hari" />
              <th className="text-left py-3 px-4">Status & Trail</th>
              <th className="text-right py-3 px-4">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {sortedFiltered.map(c => {
              const st = c.status || "approved";
              const meta = statusMeta[st] || statusMeta.approved;
              return (
                <tr key={c.id} data-testid={`cuti-row-${c.id}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors align-top">
                  <td className="py-3 px-4 font-medium text-slate-900">{c.employee_nama}</td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700">{c.jenis}</span>
                    {c.use_skd && (
                      <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-800">SKD</span>
                    )}
                    {c.surat_dokter_url && (
                      <a href={c.surat_dokter_url} target="_blank" rel="noopener noreferrer"
                        className="ml-1 text-[10px] text-sky-600 hover:underline">📎 surat</a>
                    )}
                  </td>
                  <td className="py-3 px-4 text-slate-600 text-xs">{c.tanggal_mulai} → {c.tanggal_selesai}</td>
                  <td className="py-3 px-4 text-center font-semibold">{c.jml_hari}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${meta.chip}`}>{meta.label}</span>
                    {(c.manager_approved_by || c.hr_approved_by) && (
                      <div className="mt-1.5 text-xs text-slate-500 space-y-0.5">
                        {c.manager_approved_by && <div className="flex items-center gap-1"><User className="w-3 h-3" /> Manager: <span className="font-mono text-slate-700">{c.manager_approved_by}</span></div>}
                        {c.hr_approved_by && <div className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> HR: <span className="font-mono text-slate-700">{c.hr_approved_by}</span></div>}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex gap-1">
                      {bolehApprove(c) && (st === "pending_manager" || st === "pending_hr" || st === "pending") && (
                        <>
                          <button data-testid={`btn-approve-${c.id}`} onClick={() => approve(c.id)}
                            title={st === "pending_manager" ? "Approve sebagai Manager" : "Approve sebagai HR"}
                            className="p-2 hover:bg-emerald-50 rounded-lg text-slate-400 hover:text-emerald-600"><Check className="w-4 h-4" /></button>
                          <button data-testid={`btn-reject-${c.id}`} onClick={() => reject(c.id)} title="Tolak"
                            className="p-2 hover:bg-rose-50 rounded-lg text-slate-400 hover:text-rose-600"><XCircle className="w-4 h-4" /></button>
                        </>
                      )}
                      {canDelete && <button onClick={() => del(c.id)} className="p-2 hover:bg-rose-50 rounded-lg text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </td>
                </tr>
              );
            })}
            {/* "Belum ada data cuti" saja pernah jadi kalimat yang menyesatkan: daftar
                ini disaring lingkup izin (lihat routers/cuti.py::list_cuti), jadi kosong
                bisa berarti "ada, tapi bukan jatahmu". Kalimatnya dibuat benar untuk
                kedua keadaan alih-alih menebak salah satunya. */}
            {filtered.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-sm text-slate-500">Belum ada data cuti pada lingkup yang bisa kamu lihat</td></tr>}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={cutiGuard.requestClose}>
          <div ref={cutiGuard.contentRef} className="bg-white rounded-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h3 className="font-display font-semibold text-slate-900">Ajukan Cuti</h3>
              <button onClick={cutiGuard.requestClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Karyawan</span>
                <select value={form.employee_id} onChange={e => setForm(s => ({ ...s, employee_id: e.target.value }))}
                  data-testid="cuti-employee"
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                  <option value="">— pilih —</option>
                  {emps.map(e => <option key={e.id} value={e.id}>{e.nama}</option>)}
                </select>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Jenis Cuti</span>
                  <select value={form.jenis} onChange={e => setForm(s => ({ ...s, jenis: e.target.value }))}
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    {["Tahunan","Melahirkan","Sakit","Menikah","Duka","Izin"].map(x => <option key={x}>{x}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Jumlah Hari</span>
                  <input type="number" min={1} value={form.jml_hari} onChange={e => setForm(s => ({ ...s, jml_hari: parseInt(e.target.value)||1 }))}
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Tgl Mulai</span>
                  <DatePicker value={form.tanggal_mulai} onChange={v => setForm(s => ({ ...s, tanggal_mulai: v }))}
                    className="mt-1" />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Tgl Selesai</span>
                  <DatePicker value={form.tanggal_selesai} onChange={v => setForm(s => ({ ...s, tanggal_selesai: v }))}
                    className="mt-1" />
                </label>
              </div>
              {form.jml_hari > 3 && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <span className="font-semibold">Cuti panjang (&gt;3 hari)</span> — perlu 2 approval: Manager kemudian HR (harus 2 user berbeda).
                </div>
              )}
              <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!form.use_skd}
                    onChange={e => setForm(s => ({ ...s, use_skd: e.target.checked, jenis: e.target.checked ? "Sakit" : s.jenis }))}
                    data-testid="input-use-skd"
                    className="w-4 h-4 rounded border-slate-300 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-900">Ambil dari jatah SKD (Surat Keterangan Dokter)</span>
                </label>
                {form.use_skd && (
                  <>
                    <p className="text-[11px] text-emerald-800">Wajib lampirkan surat dokter. Potong dari jatah SKD karyawan (bukan cuti biasa).</p>
                    <input type="url" placeholder="URL surat dokter (upload ke Drive/Cloud lalu paste link)"
                      value={form.surat_dokter_url}
                      onChange={e => setForm(s => ({ ...s, surat_dokter_url: e.target.value }))}
                      data-testid="input-surat-dokter"
                      className="w-full h-9 px-2 rounded border border-emerald-300 text-xs" />
                  </>
                )}
              </div>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Keterangan</span>
                <textarea value={form.keterangan} onChange={e => setForm(s => ({ ...s, keterangan: e.target.value }))}
                  className="mt-1 w-full h-20 p-3 rounded-lg border border-slate-200 text-sm resize-none" />
              </label>
              <div className="flex justify-end gap-2">
                <button onClick={cutiGuard.requestClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                <button onClick={save} data-testid="btn-save-cuti" className="h-10 px-5 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium">Ajukan</button>
              </div>
              {cutiGuard.confirming && (
                <UnsavedGuardBar onStay={cutiGuard.cancelClose} onDiscard={cutiGuard.confirmDiscard} testid="cuti-modal-guard-bar" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
